import React, { useState, useRef, useEffect } from "react";
import Button from "./ui/Button";
import { useTranslation } from "react-i18next";
import { ModalType } from "@/types/ModalTypes";
import { markdownToHtml } from "@/utils/markdown";
import { sanitizeHtml } from "@/utils/sanitize";

interface ModalProps {
  type: ModalType;
  title: string;
  body: {
    data: string | React.ReactNode;
    needTranslate: boolean;
  };
  acceptButtonText?: string;
  declineButtonText?: string;
  onAccept?: () => void;
  onDecline?: () => void;
  setIsOpen: (modal: boolean) => void;
  hideCrossIcon?: boolean;
  onClose?: () => void;
}

const Modal: React.FC<ModalProps> = ({
  title,
  body,
  acceptButtonText = "",
  declineButtonText = "",
  onAccept = () => {},
  onDecline = () => {},
  setIsOpen,
  hideCrossIcon = false,
  onClose = () => {},
}) => {
  const { t } = useTranslation();
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const bodyRef = useRef<HTMLDivElement>(null);

  const handleAccept = () => {
    onAccept();
    setIsOpen(false);
  };

  const handleDecline = () => {
    onDecline();
    setIsOpen(false);
  };

  const handleClose = () => {
    onClose();
    setIsOpen(false);
  };

  const handleScroll = () => {
    if (bodyRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = bodyRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1; // -1 for floating point precision
      setIsScrolledToBottom(isAtBottom);
    }
  };

  useEffect(() => {
    // Check initial scroll state
    handleScroll();
  }, [body]);

  // Only process with marked if it's a string, otherwise render as React component
  const renderBody = () => {
    if (typeof body.data === "string") {
      const htmlBody = markdownToHtml(body.data);
      return (
        <div
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(htmlBody) }}
          className="modal-body-content"
        />
      );
    } else {
      // Render React component as-is
      return body.data;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg p-4 sm:p-5 w-full md:w-1/2 md:max-w-[720px] flex flex-col gap-4 shadow-lg max-h-[90dvh] justify-between">
        {/* title div */}
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">{t(title)}</h2>
          {hideCrossIcon === false && (
            <button
              className="text-2xl font-bold cursor-pointer"
              onClick={handleClose}
            >
              X
            </button>
          )}
        </div>

        {/* body div */}
        <div
          ref={bodyRef}
          className="flex flex-col gap-2 overflow-y-auto max-h-[70dvh] flex-1"
          onScroll={handleScroll}
        >
          {body.needTranslate ? (
            <div className="modal-body-content">{t(body.data as string)}</div>
          ) : (
            renderBody()
          )}
        </div>

        {/* action buttons div */}
        <div className="flex justify-end gap-2 items-center ">
          {declineButtonText && declineButtonText !== "" && (
            <Button
              label={t(declineButtonText)}
              onClick={handleDecline}
              variant="secondary"
            />
          )}
          {acceptButtonText && acceptButtonText !== "" && (
            <Button
              label={t(acceptButtonText)}
              onClick={handleAccept}
              disabled={!isScrolledToBottom}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;
